import type { CustomAyanamsha } from '../utils/customAyanamsha';
import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { STUDIO_AUTH_TOKEN_KEY } from '../api/studioClient';
import { useTranslation } from 'react-i18next';
import { getLanguageFromPreferenceName, LANGUAGE_OPTIONS, normalizeLanguage, SupportedLanguage } from '../i18n/languages';
import { DateDisplayFormat, isDateDisplayFormat, setActiveDateDisplayFormat } from '../utils/dateDisplay';

export type KundliChartStyle = 'north' | 'south' | 'east';
export type DefaultChartSection = 'nadi' | 'vedic' | 'kp' | 'jaimini' | 'lal-kitab' | 'bnn';
export type AppMode = 'light' | 'dark' | 'night';
export type NodeMode = 'true' | 'mean';
export type AyanamshaMode = 'LAHIRI' | 'KP' | 'RAMAN' | 'YUKTESHWAR' | 'TRUE_CITRA' | 'TRUE_REVATI' | 'SURYASIDDHANTA' | 'FAGAN_BRADLEY' | 'CUSTOM';
export type ThemePaletteName = 'green' | 'blue' | 'yellow' | 'pink' | 'orange' | 'purple';
export type HouseSystemMode = 'KP_PLACIDUS' | 'PLACIDUS' | 'EQUAL';
export type DefaultCity = {
  id: string;
  name: string;
  displayName: string;
  coordinates: {
    latitude: number;
    longitude: number;
  };
  timezone: {
    name: string;
    offset: number;
  };
  population?: number;
  admin1?: {
    name: string;
    code?: string;
  };
  country?: {
    name: string;
    code: string;
  };
};

const CHART_STYLE_STORAGE_KEY = 'kundlistarChartStyle';
const DEFAULT_CHART_SECTION_STORAGE_KEY = 'kundlistarDefaultChartSection';
const APP_MODE_STORAGE_KEY = 'kundlistarMode';
const DATE_DISPLAY_FORMAT_STORAGE_KEY = 'kundlistarDateDisplayFormat';
const DEFAULT_CITY_STORAGE_KEY = 'kundlistarDefaultCity';
const NODE_MODE_STORAGE_KEY = 'kundlistarNodeMode';
const HOUSE_SYSTEM_STORAGE_KEY = 'kundlistarHouseSystem';
const AYANAMSHA_MODE_STORAGE_KEY = 'kundlistarAyanamshaMode';
const USE_KP_FOR_NADI_KP_STORAGE_KEY = 'kundlistarUseKpForNadiKp';
const SHOW_OUTER_PLANETS_STORAGE_KEY = 'kundlistarShowOuterPlanets';
const SAVE_HORARY_CHARTS_STORAGE_KEY = 'kundlistarSaveHoraryCharts';
const THEME_PALETTE_STORAGE_KEY = 'kundlistarThemePalette';
const PDF_TAJIK_DEFAULT_YEAR_STORAGE_KEY = 'kundlistarStudioPdfTajikDefaultYear';
const PDF_COLOR_SAME_AS_APP_STORAGE_KEY = 'kundlistarPdfColorSameAsApp';
const PDF_THEME_PALETTE_STORAGE_KEY = 'kundlistarPdfThemePalette';
const PDF_LANGUAGE_SAME_AS_APP_STORAGE_KEY = 'kundlistarStudioPdfLanguageSameAsApp';
const PDF_LANGUAGE_STORAGE_KEY = 'kundlistarStudioPdfLanguage';
const AUTH_TOKEN_KEY = STUDIO_AUTH_TOKEN_KEY;

const OUTER_PLANET_KEYS = new Set(['URANUS', 'NEPTUNE', 'PLUTO']);
const isPdfThemePalette = (value: unknown): value is ThemePaletteName => ['green','blue','yellow','pink','orange','purple'].includes(String(value));
const isPdfLanguage = (value: unknown): value is SupportedLanguage => (
  LANGUAGE_OPTIONS.some((option) => option.code === value)
);

export const resolvePdfLanguage = (
  appLanguage: unknown,
  sameAsApp: boolean,
  selectedLanguage: SupportedLanguage | null,
) => (sameAsApp || !selectedLanguage ? normalizeLanguage(String(appLanguage || 'en')) : selectedLanguage);
export const AYANAMSHA_OPTIONS: { label: string; hindiLabel: string; value: AyanamshaMode }[] = [
  { label: 'Lahiri / Chitrapaksha', hindiLabel: 'लाहिड़ी / चित्रपक्ष', value: 'LAHIRI' },
  { label: 'KP / Krishnamurti', hindiLabel: 'KP / कृष्णमूर्ति', value: 'KP' },
  { label: 'Raman', hindiLabel: 'रमन', value: 'RAMAN' },
  { label: 'Yukteshwar', hindiLabel: 'युक्तेश्वर', value: 'YUKTESHWAR' },
  { label: 'True Chitra', hindiLabel: 'ट्रू चित्रा', value: 'TRUE_CITRA' },
  { label: 'True Revati', hindiLabel: 'ट्रू रेवती', value: 'TRUE_REVATI' },
  { label: 'Suryasiddhanta', hindiLabel: 'सूर्य सिद्धांत', value: 'SURYASIDDHANTA' },
  { label: 'User Defined / Custom', hindiLabel: 'उपयोगकर्ता निर्धारित / कस्टम', value: 'CUSTOM' },
  { label: 'Fagan / Bradley', hindiLabel: 'फेगन / ब्रैडली', value: 'FAGAN_BRADLEY' },
];

export const DEFAULT_CHART_SECTION_OPTIONS: { label: string; value: DefaultChartSection }[] = [
  { label: 'Nadi', value: 'nadi' },
  { label: 'Vedic', value: 'vedic' },
  { label: 'KP', value: 'kp' },
  { label: 'Jaimini', value: 'jaimini' },
  { label: 'Lal Kitab', value: 'lal-kitab' },
  { label: 'BNN', value: 'bnn' },
];

export const isDefaultChartSection = (value: unknown): value is DefaultChartSection => (
  DEFAULT_CHART_SECTION_OPTIONS.some((option) => option.value === value)
);

export const normalizeDefaultChartSection = (value: unknown): DefaultChartSection => (
  isDefaultChartSection(value) ? value : 'nadi'
);

export const getDefaultChartSectionPath = (chartId: string, value: unknown) => {
  const base = `/chart/${chartId}`;
  const section = normalizeDefaultChartSection(value);
  if (section === 'nadi') return base;
  if (section === 'vedic') return `${base}/divisional`;
  return `${base}/${section}`;
};

export const HOUSE_SYSTEM_OPTIONS: { label: string; hindiLabel: string; value: HouseSystemMode }[] = [
  { label: 'KP Bhava Chalit / Placidus', hindiLabel: 'KP भाव चलित / प्लेसिडस', value: 'KP_PLACIDUS' },
  { label: 'Placidus cusps', hindiLabel: 'प्लेसिडस कस्प', value: 'PLACIDUS' },
  { label: 'Equal house', hindiLabel: 'इक्वल हाउस', value: 'EQUAL' },
];

export const isOuterPlanetKey = (keyOrName: unknown) => (
  OUTER_PLANET_KEYS.has(String(keyOrName || '').trim().toUpperCase())
);

export const THEME_PALETTE_OPTIONS: { label: string; hindiLabel: string; value: ThemePaletteName; swatch: string }[] = [
  { label: 'Default', hindiLabel: 'डिफॉल्ट', value: 'green', swatch: '#0F6E56' },
  { label: 'Blue', hindiLabel: 'नीला', value: 'blue', swatch: '#0b84ff' },
  { label: 'Yellow', hindiLabel: 'पीला', value: 'yellow', swatch: '#f2b900' },
  { label: 'Pink', hindiLabel: 'गुलाबी', value: 'pink', swatch: '#e84d9a' },
  { label: 'Orange', hindiLabel: 'नारंगी', value: 'orange', swatch: '#ff6a00' },
  { label: 'Purple', hindiLabel: 'बैंगनी', value: 'purple', swatch: '#8e4de8' },
];

export const THEME_PALETTE_COLORS: Record<ThemePaletteName, { main: string; light: string; dark: string; secondary: string }> = {
  green: { main: '#0F6E56', light: '#E1F5EE', dark: '#0A5240', secondary: '#085041' },
  blue: { main: '#0b84ff', light: '#64b5ff', dark: '#0758b8', secondary: '#38bdf8' },
  yellow: { main: '#f2b900', light: '#ffd54f', dark: '#8a6500', secondary: '#f59e0b' },
  pink: { main: '#e84d9a', light: '#f48fbd', dark: '#a51661', secondary: '#ec4899' },
  orange: { main: '#ff6a00', light: '#fb923c', dark: '#b45309', secondary: '#f97316' },
  purple: { main: '#8e4de8', light: '#b674ff', dark: '#5f21b5', secondary: '#a855f7' },
};

interface UserPreferencesContextValue {
  chartStyle: KundliChartStyle;
  setChartStyle: (style: KundliChartStyle) => void;
  defaultChartSection: DefaultChartSection;
  setDefaultChartSection: (section: DefaultChartSection) => void;
  mode: AppMode;
  setMode: (mode: AppMode) => void;
  dateDisplayFormat: DateDisplayFormat;
  setDateDisplayFormat: (format: DateDisplayFormat) => void;
  themePalette: ThemePaletteName;
  setThemePalette: (palette: ThemePaletteName) => void;
  defaultCity: DefaultCity | null;
  setDefaultCity: (city: DefaultCity | null) => void;
  nodeMode: NodeMode;
  setNodeMode: (mode: NodeMode) => void;
  houseSystem: HouseSystemMode;
  setHouseSystem: (system: HouseSystemMode) => void;
  customAyanamsha: CustomAyanamsha | null;
  saveCustomAyanamsha: (value: CustomAyanamsha) => Promise<void>;
  ayanamshaMode: AyanamshaMode;
  setAyanamshaMode: (mode: AyanamshaMode) => Promise<void>;
  useKpForNadiKp: boolean;
  setUseKpForNadiKp: (useKp: boolean) => void;
  showOuterPlanets: boolean;
  setShowOuterPlanets: (show: boolean) => void;
  saveHoraryCharts: boolean;
  setSaveHoraryCharts: (save: boolean) => void;
  pdfTajikDefaultYear: number;
  setPdfTajikDefaultYear: (year: number) => void;
  pdfColorSameAsApp: boolean;
  pdfThemePalette: ThemePaletteName | null;
  setPdfReportColor: (palette: ThemePaletteName | null) => void;
  pdfLanguageSameAsApp: boolean;
  setPdfLanguageSameAsApp: (sameAsApp: boolean) => void;
  pdfLanguage: SupportedLanguage | null;
  setPdfLanguage: (language: SupportedLanguage) => void;
}

const UserPreferencesContext = createContext<UserPreferencesContextValue>({
  chartStyle: 'north',
  setChartStyle: () => {},
  defaultChartSection: 'nadi',
  setDefaultChartSection: () => {},
  mode: 'light',
  setMode: () => {},
  dateDisplayFormat: 'indian',
  setDateDisplayFormat: () => {},
  themePalette: 'green',
  setThemePalette: () => {},
  defaultCity: null,
  setDefaultCity: () => {},
  nodeMode: 'true',
  setNodeMode: () => {},
  houseSystem: 'KP_PLACIDUS',
  setHouseSystem: () => {},
  customAyanamsha: null,
  saveCustomAyanamsha: async () => {},
  ayanamshaMode: 'LAHIRI',
  setAyanamshaMode: async () => {},
  useKpForNadiKp: true,
  setUseKpForNadiKp: () => {},
  showOuterPlanets: false,
  setShowOuterPlanets: () => {},
  saveHoraryCharts: false,
  setSaveHoraryCharts: () => {},
  pdfTajikDefaultYear: new Date().getFullYear(),
  setPdfTajikDefaultYear: () => {},
  pdfColorSameAsApp: true,
  pdfThemePalette: null,
  setPdfReportColor: () => {},
  pdfLanguageSameAsApp: true,
  setPdfLanguageSameAsApp: () => {},
  pdfLanguage: null,
  setPdfLanguage: () => {},
});

const isChartStyle = (value: string | null): value is KundliChartStyle => (
  value === 'north' || value === 'south' || value === 'east'
);

const isAppMode = (value: string | null): value is AppMode => value === 'light' || value === 'dark' || value === 'night';
const isNodeMode = (value: string | null): value is NodeMode => value === 'true' || value === 'mean';
const isHouseSystemMode = (value: string | null): value is HouseSystemMode => (
  value === 'KP_PLACIDUS' || value === 'PLACIDUS' || value === 'EQUAL'
);
const isAyanamshaMode = (value: string | null): value is AyanamshaMode => (
  AYANAMSHA_OPTIONS.some((option) => option.value === value)
);
const isThemePalette = (value: string | null): value is ThemePaletteName => (
  THEME_PALETTE_OPTIONS.some((option) => option.value === value)
);
const toThemePaletteFromHex = (value: unknown): ThemePaletteName | null => {
  const normalized = String(value || '').trim().toLowerCase();
  if (!/^#[0-9a-f]{6}$/.test(normalized)) return null;
  const match = THEME_PALETTE_OPTIONS.find((option) => option.swatch.toLowerCase() === normalized);
  return match?.value || null;
};
const toServerChartStyle = (style: KundliChartStyle) => style.charAt(0).toUpperCase() + style.slice(1);

const toLocalChartStyle = (style: unknown): KundliChartStyle | null => {
  const normalized = String(style || '').toLowerCase();
  return isChartStyle(normalized) ? normalized : null;
};

const syncPreferencesToServer = (preferences: Record<string, unknown>) => {
  if (localStorage.getItem(AUTH_TOKEN_KEY)) {
    axios.put('/users/profile', { preferences }).catch(() => {});
  }
};

export const UserPreferencesProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { i18n } = useTranslation();
  const [chartStyle, setChartStyleState] = useState<KundliChartStyle>(() => {
    const storedChartStyle = localStorage.getItem(CHART_STYLE_STORAGE_KEY);
    return isChartStyle(storedChartStyle) ? storedChartStyle : 'north';
  });
  const [defaultChartSection, setDefaultChartSectionState] = useState<DefaultChartSection>(() => (
    normalizeDefaultChartSection(localStorage.getItem(DEFAULT_CHART_SECTION_STORAGE_KEY))
  ));
  const [mode, setModeState] = useState<AppMode>(() => {
    const storedMode = localStorage.getItem(APP_MODE_STORAGE_KEY);
    return isAppMode(storedMode) ? storedMode : 'light';
  });
  const [dateDisplayFormat, setDateDisplayFormatState] = useState<DateDisplayFormat>(() => {
    const storedFormat = localStorage.getItem(DATE_DISPLAY_FORMAT_STORAGE_KEY);
    const initialFormat = isDateDisplayFormat(storedFormat) ? storedFormat : 'indian';
    setActiveDateDisplayFormat(initialFormat);
    return initialFormat;
  });
  const [themePalette, setThemePaletteState] = useState<ThemePaletteName>(() => {
    const storedPalette = localStorage.getItem(THEME_PALETTE_STORAGE_KEY);
    return isThemePalette(storedPalette) ? storedPalette : 'green';
  });
  const [defaultCity, setDefaultCityState] = useState<DefaultCity | null>(() => {
    const storedDefaultCity = localStorage.getItem(DEFAULT_CITY_STORAGE_KEY);
    if (!storedDefaultCity) return null;
    try {
      return JSON.parse(storedDefaultCity);
    } catch {
      localStorage.removeItem(DEFAULT_CITY_STORAGE_KEY);
      return null;
    }
  });
  const [nodeMode, setNodeModeState] = useState<NodeMode>(() => {
    const storedNodeMode = localStorage.getItem(NODE_MODE_STORAGE_KEY);
    return isNodeMode(storedNodeMode) ? storedNodeMode : 'true';
  });
  const [houseSystem, setHouseSystemState] = useState<HouseSystemMode>(() => {
    const storedHouseSystem = localStorage.getItem(HOUSE_SYSTEM_STORAGE_KEY);
    return isHouseSystemMode(storedHouseSystem) ? storedHouseSystem : 'KP_PLACIDUS';
  });
  const [customAyanamsha, setCustomAyanamshaState] = useState<CustomAyanamsha | null>(null);
  const [ayanamshaMode, setAyanamshaModeState] = useState<AyanamshaMode>(() => {
    const storedAyanamshaMode = localStorage.getItem(AYANAMSHA_MODE_STORAGE_KEY);
    return storedAyanamshaMode !== 'CUSTOM' && isAyanamshaMode(storedAyanamshaMode) ? storedAyanamshaMode : 'LAHIRI';
  });
  const [useKpForNadiKp, setUseKpForNadiKpState] = useState(() => (
    localStorage.getItem(USE_KP_FOR_NADI_KP_STORAGE_KEY) !== 'false'
  ));
  const [showOuterPlanets, setShowOuterPlanetsState] = useState(() => (
    localStorage.getItem(SHOW_OUTER_PLANETS_STORAGE_KEY) === 'true'
  ));
  const [saveHoraryCharts, setSaveHoraryChartsState] = useState(() => (
    localStorage.getItem(SAVE_HORARY_CHARTS_STORAGE_KEY) === 'true'
  ));
  const [pdfTajikDefaultYear, setPdfTajikDefaultYearState] = useState(() => {
    const currentYear = new Date().getFullYear();
    const storedYear = Number(localStorage.getItem(PDF_TAJIK_DEFAULT_YEAR_STORAGE_KEY));
    return Number.isInteger(storedYear) && storedYear >= currentYear - 2 && storedYear <= currentYear + 2 ? storedYear : currentYear;
  });
  const [pdfColorSameAsApp, setPdfColorSameAsAppState] = useState(() => localStorage.getItem(PDF_COLOR_SAME_AS_APP_STORAGE_KEY) !== 'false');
  const [pdfThemePalette, setPdfThemePaletteState] = useState<ThemePaletteName | null>(() => {
    const saved = localStorage.getItem(PDF_THEME_PALETTE_STORAGE_KEY);
    return isPdfThemePalette(saved) ? saved : null;
  });
  const [pdfLanguageSameAsApp, setPdfLanguageSameAsAppState] = useState(() => (
    localStorage.getItem(PDF_LANGUAGE_SAME_AS_APP_STORAGE_KEY) !== 'false'
  ));
  const [pdfLanguage, setPdfLanguageState] = useState<SupportedLanguage | null>(() => {
    const storedLanguage = localStorage.getItem(PDF_LANGUAGE_STORAGE_KEY);
    return isPdfLanguage(storedLanguage) ? storedLanguage : null;
  });

  useEffect(() => {
    document.documentElement.dataset.theme = mode;
    const pageBackground = mode === 'night' ? '#050805' : mode === 'dark' ? '#07130D' : '#F6F7F6';
    document.documentElement.style.backgroundColor = pageBackground;
    document.body.style.backgroundColor = pageBackground;
  }, [mode]);

  useEffect(() => {
    const syncServerDefaultCity = async () => {
      const requestedToken = localStorage.getItem(AUTH_TOKEN_KEY);
      if (!requestedToken) return;
      try {
        const response = await axios.get('/users/profile');
        if (localStorage.getItem(AUTH_TOKEN_KEY) !== requestedToken) return;
        const serverUser = response.data?.data?.user || {};
        const serverPreferences = serverUser.preferences || {};
        if (typeof serverPreferences.pdfColorSameAsApp === 'boolean') {
          setPdfColorSameAsAppState(serverPreferences.pdfColorSameAsApp);
          localStorage.setItem(PDF_COLOR_SAME_AS_APP_STORAGE_KEY, String(serverPreferences.pdfColorSameAsApp));
        }
        if (isPdfThemePalette(serverPreferences.pdfThemePalette)) {
          setPdfThemePaletteState(serverPreferences.pdfThemePalette);
          localStorage.setItem(PDF_THEME_PALETTE_STORAGE_KEY, serverPreferences.pdfThemePalette);
        }

        setCustomAyanamshaState(serverPreferences.customAyanamsha || null);
        const serverChartStyle = toLocalChartStyle(serverPreferences.chartStyle);
        const hasDefaultChartSection = Object.prototype.hasOwnProperty.call(serverPreferences, 'defaultChartSection');
        const serverMode = isAppMode(serverPreferences.themeMode) ? serverPreferences.themeMode : null;
        const serverDateDisplayFormat = isDateDisplayFormat(serverPreferences.dateDisplayFormat)
          ? serverPreferences.dateDisplayFormat
          : null;
        const serverThemePalette = isThemePalette(serverPreferences.themePalette)
          ? serverPreferences.themePalette
          : toThemePaletteFromHex(serverPreferences.themePalette)
            || toThemePaletteFromHex(serverPreferences.themeColor)
            || toThemePaletteFromHex(serverUser.reportBranding?.themeColor);
        const serverNodeMode = isNodeMode(serverPreferences.nodeMode) ? serverPreferences.nodeMode : null;
        const serverHouseSystem = isHouseSystemMode(serverPreferences.houseSystem) ? serverPreferences.houseSystem : null;
        const serverAyanamshaMode = isAyanamshaMode(String(serverPreferences.ayanamshaMode || '').toUpperCase())
          ? String(serverPreferences.ayanamshaMode).toUpperCase() as AyanamshaMode
          : null;
        const serverUseKpForNadiKp = serverPreferences.useKpForNadiKp !== false;
        const serverShowOuterPlanets = serverPreferences.showOuterPlanets === true;
        const serverSaveHoraryCharts = serverPreferences.saveHoraryCharts === true;
        const currentYear = new Date().getFullYear();
        const requestedPdfTajikYear = Number(serverPreferences.pdfTajikDefaultYear);
        const serverPdfTajikDefaultYear = Number.isInteger(requestedPdfTajikYear)
          && requestedPdfTajikYear >= currentYear - 2
          && requestedPdfTajikYear <= currentYear + 2
          ? requestedPdfTajikYear
          : currentYear;
        const hasPdfLanguageSameAsApp = Object.prototype.hasOwnProperty.call(serverPreferences, 'pdfLanguageSameAsApp');
        const hasPdfLanguage = Object.prototype.hasOwnProperty.call(serverPreferences, 'pdfLanguage');
        const serverDefaultCity = serverPreferences.defaultCity || null;

        if (serverChartStyle) {
          setChartStyleState(serverChartStyle);
          localStorage.setItem(CHART_STYLE_STORAGE_KEY, serverChartStyle);
        }
        if (hasDefaultChartSection) {
          const nextDefaultChartSection = normalizeDefaultChartSection(serverPreferences.defaultChartSection);
          setDefaultChartSectionState(nextDefaultChartSection);
          localStorage.setItem(DEFAULT_CHART_SECTION_STORAGE_KEY, nextDefaultChartSection);
        }
        if (serverMode) {
          setModeState(serverMode);
          localStorage.setItem(APP_MODE_STORAGE_KEY, serverMode);
        }
        if (serverDateDisplayFormat) {
          setDateDisplayFormatState(serverDateDisplayFormat);
          setActiveDateDisplayFormat(serverDateDisplayFormat);
          localStorage.setItem(DATE_DISPLAY_FORMAT_STORAGE_KEY, serverDateDisplayFormat);
        }
        if (serverThemePalette) {
          setThemePaletteState(serverThemePalette);
          localStorage.setItem(THEME_PALETTE_STORAGE_KEY, serverThemePalette);
        }
        if (serverNodeMode) {
          setNodeModeState(serverNodeMode);
          localStorage.setItem(NODE_MODE_STORAGE_KEY, serverNodeMode);
        }
        if (serverHouseSystem) {
          setHouseSystemState(serverHouseSystem);
          localStorage.setItem(HOUSE_SYSTEM_STORAGE_KEY, serverHouseSystem);
        }
        if (serverAyanamshaMode) {
          setAyanamshaModeState(serverAyanamshaMode);
          localStorage.setItem(AYANAMSHA_MODE_STORAGE_KEY, serverAyanamshaMode);
        }
        setUseKpForNadiKpState(serverUseKpForNadiKp);
        localStorage.setItem(USE_KP_FOR_NADI_KP_STORAGE_KEY, String(serverUseKpForNadiKp));
        setShowOuterPlanetsState(serverShowOuterPlanets);
        localStorage.setItem(SHOW_OUTER_PLANETS_STORAGE_KEY, String(serverShowOuterPlanets));
        setSaveHoraryChartsState(serverSaveHoraryCharts);
        localStorage.setItem(SAVE_HORARY_CHARTS_STORAGE_KEY, String(serverSaveHoraryCharts));
        setPdfTajikDefaultYearState(serverPdfTajikDefaultYear);
        localStorage.setItem(PDF_TAJIK_DEFAULT_YEAR_STORAGE_KEY, String(serverPdfTajikDefaultYear));
        if (hasPdfLanguageSameAsApp) {
          const nextSameAsApp = serverPreferences.pdfLanguageSameAsApp !== false;
          setPdfLanguageSameAsAppState(nextSameAsApp);
          localStorage.setItem(PDF_LANGUAGE_SAME_AS_APP_STORAGE_KEY, String(nextSameAsApp));
        }
        if (hasPdfLanguage && isPdfLanguage(serverPreferences.pdfLanguage)) {
          setPdfLanguageState(serverPreferences.pdfLanguage);
          localStorage.setItem(PDF_LANGUAGE_STORAGE_KEY, serverPreferences.pdfLanguage);
        }
        const localLanguage = localStorage.getItem('kundlistarLanguage');
        if (localLanguage) {
          i18n.changeLanguage(getLanguageFromPreferenceName(localLanguage));
        } else if (serverPreferences.language) {
          i18n.changeLanguage(getLanguageFromPreferenceName(serverPreferences.language));
        }
        if (serverDefaultCity?.id) {
          setDefaultCityState(serverDefaultCity);
          localStorage.setItem(DEFAULT_CITY_STORAGE_KEY, JSON.stringify(serverDefaultCity));
        } else if (serverDefaultCity === null) {
          setDefaultCityState(null);
          localStorage.removeItem(DEFAULT_CITY_STORAGE_KEY);
        }
      } catch {
        // Keep the local preference if profile sync is temporarily unavailable.
      }
    };

    syncServerDefaultCity();
  }, [i18n]);

  const setChartStyle = useCallback((style: KundliChartStyle) => {
    setChartStyleState(style);
    localStorage.setItem(CHART_STYLE_STORAGE_KEY, style);
    syncPreferencesToServer({ chartStyle: toServerChartStyle(style) });
  }, []);

  const setDefaultChartSection = useCallback((section: DefaultChartSection) => {
    const nextSection = normalizeDefaultChartSection(section);
    setDefaultChartSectionState(nextSection);
    localStorage.setItem(DEFAULT_CHART_SECTION_STORAGE_KEY, nextSection);
    syncPreferencesToServer({ defaultChartSection: nextSection });
  }, []);

  const setMode = useCallback((nextMode: AppMode) => {
    setModeState(nextMode);
    localStorage.setItem(APP_MODE_STORAGE_KEY, nextMode);
    syncPreferencesToServer({ themeMode: nextMode });
  }, []);

  const setDateDisplayFormat = useCallback((nextFormat: DateDisplayFormat) => {
    setDateDisplayFormatState(nextFormat);
    setActiveDateDisplayFormat(nextFormat);
    localStorage.setItem(DATE_DISPLAY_FORMAT_STORAGE_KEY, nextFormat);
    syncPreferencesToServer({ dateDisplayFormat: nextFormat });
  }, []);

  const setThemePalette = useCallback((nextPalette: ThemePaletteName) => {
    setThemePaletteState(nextPalette);
    localStorage.setItem(THEME_PALETTE_STORAGE_KEY, nextPalette);
    syncPreferencesToServer({ themePalette: nextPalette });
  }, []);

  const setDefaultCity = useCallback((city: DefaultCity | null) => {
    setDefaultCityState(city);
    if (city) {
      localStorage.setItem(DEFAULT_CITY_STORAGE_KEY, JSON.stringify(city));
    } else {
      localStorage.removeItem(DEFAULT_CITY_STORAGE_KEY);
    }
    syncPreferencesToServer({ defaultCity: city });
  }, []);

  const setNodeMode = useCallback((nextNodeMode: NodeMode) => {
    setNodeModeState(nextNodeMode);
    localStorage.setItem(NODE_MODE_STORAGE_KEY, nextNodeMode);
    syncPreferencesToServer({ nodeMode: nextNodeMode });
  }, []);

  const setHouseSystem = useCallback((nextHouseSystem: HouseSystemMode) => {
    setHouseSystemState(nextHouseSystem);
    localStorage.setItem(HOUSE_SYSTEM_STORAGE_KEY, nextHouseSystem);
    syncPreferencesToServer({ houseSystem: nextHouseSystem });
  }, []);

  useEffect(() => {
    const syncProfileAyanamsha = (event: Event) => {
      try {
        const user = localStorage.getItem(AUTH_TOKEN_KEY) ? (event as CustomEvent).detail : null;
        setCustomAyanamshaState(user?.preferences?.customAyanamsha || null);
        if (user?.preferences?.ayanamshaMode && isAyanamshaMode(user.preferences.ayanamshaMode)) setAyanamshaModeState(user.preferences.ayanamshaMode);
        else setAyanamshaModeState(mode => mode === 'CUSTOM' ? 'LAHIRI' : mode);
      } catch { setCustomAyanamshaState(null); }
    };
    window.addEventListener('kundlistar-profile-changed', syncProfileAyanamsha);
    return () => window.removeEventListener('kundlistar-profile-changed', syncProfileAyanamsha);
  }, []);

  const saveCustomAyanamsha = useCallback(async (value: CustomAyanamsha) => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) throw new Error('Sign in to save custom ayanamsha to your profile.');
    const response = await axios.put('/users/profile', { preferences: { ayanamshaMode: 'CUSTOM', customAyanamsha: value } });
    if (localStorage.getItem(AUTH_TOKEN_KEY) !== token) throw new Error('Account changed while saving. Please sign in and try again.');
    window.dispatchEvent(new CustomEvent('kundlistar-profile-changed', { detail: response.data?.data?.user }));
    setCustomAyanamshaState(value);
    setAyanamshaModeState('CUSTOM');
    localStorage.setItem(AYANAMSHA_MODE_STORAGE_KEY, 'CUSTOM');
  }, []);

  const setAyanamshaMode = useCallback(async (nextAyanamshaMode: AyanamshaMode) => {
    if (nextAyanamshaMode === 'CUSTOM') throw new Error('Use Save custom ayanamsha to activate a custom definition.');
    if (localStorage.getItem(AUTH_TOKEN_KEY)) {
      const response = await axios.put('/users/profile', { preferences: { ayanamshaMode: nextAyanamshaMode } });
      window.dispatchEvent(new CustomEvent('kundlistar-profile-changed', { detail: response.data?.data?.user }));
    }
    setAyanamshaModeState(nextAyanamshaMode);
    localStorage.setItem(AYANAMSHA_MODE_STORAGE_KEY, nextAyanamshaMode);
  }, []);

  const setUseKpForNadiKp = useCallback((useKp: boolean) => {
    setUseKpForNadiKpState(useKp);
    localStorage.setItem(USE_KP_FOR_NADI_KP_STORAGE_KEY, String(useKp));
    syncPreferencesToServer({ useKpForNadiKp: useKp });
  }, []);

  const setShowOuterPlanets = useCallback((show: boolean) => {
    setShowOuterPlanetsState(show);
    localStorage.setItem(SHOW_OUTER_PLANETS_STORAGE_KEY, String(show));
    syncPreferencesToServer({ showOuterPlanets: show });
  }, []);

  const setSaveHoraryCharts = useCallback((save: boolean) => {
    setSaveHoraryChartsState(save);
    localStorage.setItem(SAVE_HORARY_CHARTS_STORAGE_KEY, String(save));
    syncPreferencesToServer({ saveHoraryCharts: save });
  }, []);

  const setPdfTajikDefaultYear = useCallback((year: number) => {
    const currentYear = new Date().getFullYear();
    const nextYear = Number.isInteger(year) && year >= currentYear - 2 && year <= currentYear + 2 ? year : currentYear;
    setPdfTajikDefaultYearState(nextYear);
    localStorage.setItem(PDF_TAJIK_DEFAULT_YEAR_STORAGE_KEY, String(nextYear));
    syncPreferencesToServer({ pdfTajikDefaultYear: nextYear });
  }, []);

  const setPdfReportColor = useCallback((palette: ThemePaletteName | null) => {
    if (palette !== null && !isPdfThemePalette(palette)) return;
    const sameAsApp = palette === null;
    setPdfColorSameAsAppState(sameAsApp);
    localStorage.setItem(PDF_COLOR_SAME_AS_APP_STORAGE_KEY, String(sameAsApp));
    if (palette) {
      setPdfThemePaletteState(palette);
      localStorage.setItem(PDF_THEME_PALETTE_STORAGE_KEY, palette);
    }
    syncPreferencesToServer({ pdfColorSameAsApp: sameAsApp, ...(palette ? { pdfThemePalette: palette } : {}) });
  }, []);

  const setPdfLanguageSameAsApp = useCallback((sameAsApp: boolean) => {
    setPdfLanguageSameAsAppState(sameAsApp);
    localStorage.setItem(PDF_LANGUAGE_SAME_AS_APP_STORAGE_KEY, String(sameAsApp));
    syncPreferencesToServer({ pdfLanguageSameAsApp: sameAsApp });
  }, []);

  const setPdfLanguage = useCallback((nextLanguage: SupportedLanguage) => {
    if (!isPdfLanguage(nextLanguage)) return;
    setPdfLanguageState(nextLanguage);
    localStorage.setItem(PDF_LANGUAGE_STORAGE_KEY, nextLanguage);
    syncPreferencesToServer({ pdfLanguage: nextLanguage });
  }, []);

  const value = useMemo(
    () => ({
      chartStyle,
      setChartStyle,
      defaultChartSection,
      setDefaultChartSection,
      mode,
      setMode,
      dateDisplayFormat,
      setDateDisplayFormat,
      themePalette,
      setThemePalette,
      defaultCity,
      setDefaultCity,
      nodeMode,
      setNodeMode,
      houseSystem,
      setHouseSystem,
      customAyanamsha,
      saveCustomAyanamsha,
      ayanamshaMode,
      setAyanamshaMode,
      useKpForNadiKp,
      setUseKpForNadiKp,
      showOuterPlanets,
      setShowOuterPlanets,
      saveHoraryCharts,
      setSaveHoraryCharts,
      pdfTajikDefaultYear,
      setPdfTajikDefaultYear,
      pdfColorSameAsApp, pdfThemePalette, setPdfReportColor,
      pdfLanguageSameAsApp,
      setPdfLanguageSameAsApp,
      pdfLanguage,
      setPdfLanguage
    }),
    [pdfColorSameAsApp, pdfThemePalette, setPdfReportColor, customAyanamsha, saveCustomAyanamsha, ayanamshaMode, chartStyle, dateDisplayFormat, defaultChartSection, defaultCity, houseSystem, mode, nodeMode, pdfLanguage, pdfLanguageSameAsApp, pdfTajikDefaultYear, saveHoraryCharts, showOuterPlanets, themePalette, useKpForNadiKp, setAyanamshaMode, setChartStyle, setDateDisplayFormat, setDefaultChartSection, setDefaultCity, setHouseSystem, setMode, setNodeMode, setPdfLanguage, setPdfLanguageSameAsApp, setPdfTajikDefaultYear, setSaveHoraryCharts, setShowOuterPlanets, setThemePalette, setUseKpForNadiKp]
  );

  return (
    <UserPreferencesContext.Provider value={value}>
      {children}
    </UserPreferencesContext.Provider>
  );
};

export function useUserPreferences() {
  return useContext(UserPreferencesContext);
}
